import React, { useEffect, useRef, useState } from 'react';

import Tippy from '@tippyjs/react';

import { formatTimeShortRelativeToToday } from 'lib/helpers/utility';

import Avatar from 'components/shared/Avatar/Avatar';
import Button from 'components/shared/Button/Button';
import Icon from 'components/shared/Icon/Icon';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Textarea from 'components/shared/Textarea/Textarea';
import Typography from 'components/shared/Typography/Typography';

const Comment = ({ comment, onDelete, onSave, creatorId }) => {
  const textareaRef = useRef(null);

  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [currentComment, setCurrentComment] = useState(comment || {});

  const { creator, created_at, updated_at, value } = currentComment;
  const fullName = `${creator.f_name} ${creator.l_name}`;

  useEffect(() => {
    setCurrentComment(comment);
  }, [comment]);

  const onEditHandler = () => {
    setIsEditing(true);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const length = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    }, 100);
  };

  const onChangeHandler = (e) => {
    setCurrentComment({ ...currentComment, value: e.target.value });
  };

  const onCancelHandler = () => {
    setIsEditing(false);
    setIsHovered(false);
    setCurrentComment(comment);
  };

  const onToggleDeleteHandler = () => setDeleteWarning((prev) => !prev);

  const onSaveHandler = async () => {
    await onSave(currentComment);
    onCancelHandler();
  };

  const onMouseOverHandler = () => {
    if (creatorId === creator?.id) {
      setIsHovered(true);
    }
  };

  const onMouseOutHandler = () => {
    if (creatorId === creator?.id) {
      setIsHovered(false);
    }
  };

  return (
    <div
      className="isolate flex h-fit w-full flex-col items-start gap-2 rounded-lg border border-solid border-neutral-200 px-4 py-3"
      onMouseOver={onMouseOverHandler}
      onMouseOut={onMouseOutHandler}>
      <div className="flex w-full flex-row items-center gap-2">
        <Avatar className="h-8 w-8" fullName={fullName} />
        <div className="flex w-full items-center justify-between">
          <Typography variant="p" color="neutral-800" size="xs">
            {fullName}
          </Typography>
          {!isHovered || isEditing ? (
            <Typography color="neutral-500" size="small">
              {formatTimeShortRelativeToToday(created_at) +
                (updated_at && created_at !== updated_at ? ` (edited)` : '')}
            </Typography>
          ) : (
            <div className="flex flex-row items-center gap-2 p-0">
              <Tippy content="Edit" className="tippy-dark">
                <div
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md hover:bg-neutral-75"
                  onClick={onEditHandler}>
                  <Icon
                    icon="edit-2-mono"
                    color="neutral"
                    size={16}
                    shade={600}
                    className="cursor-pointer"
                  />
                </div>
              </Tippy>
              <Tippy content="Delete" className="tippy-dark">
                <div
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md hover:bg-neutral-75"
                  onClick={onToggleDeleteHandler}>
                  <Icon
                    icon="trash-mono"
                    color="neutral"
                    size={14}
                    shade={600}
                    className="cursor-pointer"
                  />
                </div>
              </Tippy>
            </div>
          )}
        </div>
      </div>
      {isEditing ? (
        <>
          <Textarea
            forwardedRef={textareaRef}
            className="w-full"
            bodyClassName="w-full border-none"
            value={value}
            onChange={onChangeHandler}
          />
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              size="small"
              style="outlined"
              color="neutral"
              text="Cancel"
              onClick={onCancelHandler}
            />
            <Button size="small" text="Save" onClick={onSaveHandler} />
          </div>
        </>
      ) : (
        <Typography color="neutral-800">{value}</Typography>
      )}
      <Confirm
        handleOpen={deleteWarning}
        handleClose={onToggleDeleteHandler}
        handleContinue={() => {
          onDelete(comment.id);
          onToggleDeleteHandler();
        }}
        title="Delete this comment?"
        message="You cannot do undo this action"
        iconColor="danger"
        variant="danger"
        icon="message-remove-mono"
      />
    </div>
  );
};

export default Comment;
